import { Button, Modal, Skeleton, useToast } from "@design";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type SpaceSummary, schemas } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { NODE_TYPES } from "../nodeTypes";
import { type Proposal, useAcceptProposal, useEditableSpaces, useInboxErrorMessage } from "./useInbox";

// Field rules from the GENERATED ProposalAccept schema (allowed relations,
// types, visibilities, name lengths): the backend stays the source of truth.
const api = schemas.zProposalAccept.shape;
const RELATIONS = api.relation.unwrap().unwrap().options;
const VISIBILITIES = ["private", "members", "org"] as const;
// Relations that carry a role; the backend ignores a role on any other (ROLE_RELATIONS in extract_prompt.py).
const ROLE_RELATIONS: ReadonlySet<string> = new Set(["works_on", "leads", "owns"]);

/**
 * New pages are for the space's members, except in your personal space. (Not by member count:
 * that only counts people added directly, and misses teams the space is shared with.)
 */
export function defaultVisibility(space: SpaceSummary | undefined): (typeof VISIBILITIES)[number] {
  return !space || space.isPersonal ? "private" : "members";
}

function acceptSchema(nameRequired: string) {
  const name = z.string().trim().min(1, nameRequired).max(200);
  return z.object({
    subjectName: name,
    subjectType: z.enum(NODE_TYPES),
    subjectAliases: z.string(),
    relation: z.enum(RELATIONS),
    objectName: name,
    objectType: z.enum(NODE_TYPES),
    objectAliases: z.string(),
    role: z.string().trim().max(80),
    spaceUuid: z.string().min(1),
    newPageVisibility: z.enum(VISIBILITIES),
  });
}

function asNodeType(value: string): (typeof NODE_TYPES)[number] {
  return (NODE_TYPES as readonly string[]).includes(value) ? (value as (typeof NODE_TYPES)[number]) : "concept";
}

/** "Ravi, RS" -> ["Ravi", "RS"] */
function splitNames(value: string): string[] {
  return value
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

/**
 * Review a proposed fact before adding it: correct names, types or the
 * relationship, confirm other names for new pages (they change what links
 * resolve to, so nothing is written unseen), pick the space to write into
 * (ones you can edit; default: the chat's space, else your personal space)
 * and who sees new pages. Mount only while open.
 */
export function AcceptProposalModal({ proposal, onClose }: Readonly<{ proposal: Proposal; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const spaces = useEditableSpaces();
  if (!spaces.data) {
    // The form takes its default space from this list, so it waits for it.
    return (
      <Modal
        open
        onClose={onClose}
        title={t("accept.title")}
        actions={
          <Button variant="secondary" onClick={onClose}>
            {t("accept.cancel")}
          </Button>
        }
      >
        {spaces.isError ? (
          <Text role="alert" tone="error">
            {t("accept.spacesFailed")}
          </Text>
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </Modal>
    );
  }
  return <AcceptForm proposal={proposal} spaces={spaces.data} onClose={onClose} />;
}

function AcceptForm({
  proposal,
  spaces,
  onClose,
}: Readonly<{ proposal: Proposal; spaces: SpaceSummary[]; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useInboxErrorMessage();
  const accept = useAcceptProposal();
  const options = spaces.map((s) => ({ value: s.uuid, label: s.name }));
  const defaultSpace =
    options.find((o) => o.value === proposal.chatSpaceUuid)?.value ??
    spaces.find((s) => s.isPersonal)?.uuid ??
    options[0]?.value ??
    "";

  const form = useAppForm({
    defaultValues: {
      subjectName: proposal.subject.name,
      subjectType: asNodeType(proposal.subject.type),
      subjectAliases: proposal.subject.aliases.join(", "),
      relation: proposal.relation,
      objectName: proposal.object.name,
      objectType: asNodeType(proposal.object.type),
      objectAliases: proposal.object.aliases.join(", "),
      role: proposal.role ?? "",
      spaceUuid: defaultSpace,
      newPageVisibility: defaultVisibility(spaces.find((s) => s.uuid === defaultSpace)),
    },
    validators: { onChange: acceptSchema(t("accept.nameRequired")) },
    onSubmit: async ({ value }) => {
      try {
        const res = await accept.mutateAsync({
          path: { proposal_uuid: proposal.uuid },
          body: {
            ...value,
            subjectAliases: splitNames(value.subjectAliases),
            objectAliases: splitNames(value.objectAliases),
            role: ROLE_RELATIONS.has(value.relation) ? value.role.trim() : null,
          },
          throwOnError: true,
        });
        const space = spaces.find((s) => s.uuid === res.data.spaceUuid)?.name ?? "";
        toast.success(t("accept.added", { space, count: res.data.pages.length }));
        onClose();
      } catch (error) {
        toast.error(message(error, t("accept.failed")));
      }
    },
  });

  const typeOptions = NODE_TYPES.map((type) => ({ value: type, label: t(`type.${type}`) }));
  const newPages = [proposal.subject, proposal.object].filter((e) => !e.node).map((e) => e.name);

  return (
    <Modal
      open
      onClose={onClose}
      title={t("accept.title")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("accept.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {t("accept.submit")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={t("accept.title")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_10rem]">
          <form.AppField name="subjectName">
            {(field) => <field.TextField label={t("accept.subject")} required />}
          </form.AppField>
          <form.AppField name="subjectType">
            {(field) => <field.SelectField label={t("accept.subjectType")} options={typeOptions} />}
          </form.AppField>
        </div>
        {!proposal.subject.node && (
          <form.AppField name="subjectAliases">
            {(field) => <field.TextField label={t("accept.subjectAliases")} placeholder={t("accept.aliasesHint")} />}
          </form.AppField>
        )}
        <form.AppField name="relation">
          {(field) => (
            <field.SelectField
              label={t("accept.relation")}
              options={RELATIONS.map((r) => ({ value: r, label: t(`relation.${r}`) }))}
            />
          )}
        </form.AppField>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_10rem]">
          <form.AppField name="objectName">
            {(field) => <field.TextField label={t("accept.object")} required />}
          </form.AppField>
          <form.AppField name="objectType">
            {(field) => <field.SelectField label={t("accept.objectType")} options={typeOptions} />}
          </form.AppField>
        </div>
        {!proposal.object.node && (
          <form.AppField name="objectAliases">
            {(field) => <field.TextField label={t("accept.objectAliases")} placeholder={t("accept.aliasesHint")} />}
          </form.AppField>
        )}
        <form.Subscribe selector={(s) => s.values.relation}>
          {(relation) =>
            ROLE_RELATIONS.has(relation) && (
              <form.AppField name="role">
                {(field) => <field.TextField label={t("accept.role")} placeholder={t("accept.roleHint")} />}
              </form.AppField>
            )
          }
        </form.Subscribe>
        <form.AppField
          name="spaceUuid"
          listeners={{
            onChange: ({ value }) =>
              form.setFieldValue("newPageVisibility", defaultVisibility(spaces.find((s) => s.uuid === value))),
          }}
        >
          {(field) => <field.SelectField label={t("accept.space")} options={options} />}
        </form.AppField>
        <form.AppField name="newPageVisibility">
          {(field) => (
            <field.SelectField
              label={t("accept.visibility")}
              options={VISIBILITIES.map((v) => ({ value: v, label: t(`visibility.${v}`) }))}
            />
          )}
        </form.AppField>
        {newPages.length > 0 && (
          <Text variant="body-sm" tone="secondary">
            {t("accept.willCreate", { names: formatList(newPages) })}
          </Text>
        )}
      </form>
    </Modal>
  );
}
